import React, { useContext, useEffect, useState } from 'react';
import { LanguageContext } from '@/App';
import axios from 'axios';
import { Calendar, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Events = () => {
  const { language } = useContext(LanguageContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Event Calendar',
      subtitle: 'Stay updated with upcoming festivals, poojas, and spiritual celebrations',
      noEvents: 'No events scheduled at the moment. Please check back later.',
      loading: 'Loading events...',
    },
    hi: {
      title: 'कार्यक्रम कैलेंडर',
      subtitle: 'आगामी त्योहारों, पूजाओं और आध्यात्मिक उत्सवों के साथ अपडेट रहें',
      noEvents: 'इस समय कोई कार्यक्रम निर्धारित नहीं है। कृपया बाद में फिर से जांच करें।',
      loading: 'कार्यक्रम लोड हो रहे हैं...',
    },
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const t = content[language];

  // Sample upcoming events if no events in DB
  const sampleEvents = [
    {
      id: '1',
      title: 'Mahavir Jayanti',
      title_hi: 'महावीर जयंती',
      description: 'Celebration of Lord Mahavir\'s birth anniversary with special prayers and rituals',
      description_hi: 'भगवान महावीर की जयंती का विशेष प्रार्थनाओं और अनुष्ठानों के साथ उत्सव',
      date: 'April 2025',
      time: '6:00 AM - 8:00 PM',
    },
    {
      id: '2',
      title: 'Paryushana Parva',
      title_hi: 'पर्युषण पर्व',
      description: 'The most important Jain festival with 8 days of fasting, meditation and spiritual practices',
      description_hi: '8 दिनों के उपवास, ध्यान और आध्यात्मिक अभ्यास के साथ सबसे महत्वपूर्ण जैन त्योहार',
      date: 'August 2025',
      time: 'All Day',
    },
  ];

  const displayEvents = events.length > 0 ? events : sampleEvents;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="hero-section py-20 px-4" data-testid="events-hero">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6" data-testid="events-title">
            {t.title}
          </h1>
          <p className="text-base md:text-lg text-amber-800" data-testid="events-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          {loading ? (
            <p className="text-center text-amber-800 text-lg" data-testid="events-loading">{t.loading}</p>
          ) : displayEvents.length === 0 ? (
            <p className="text-center text-amber-800 text-lg" data-testid="events-empty">{t.noEvents}</p>
          ) : (
            <div className="space-y-6">
              {displayEvents.map((event, index) => (
                <div
                  key={event.id || index}
                  className="offering-card p-6 rounded-xl shadow-md"
                  data-testid={`event-card-${index}`}
                >
                  <h3 className="text-2xl font-bold text-amber-900 mb-3">
                    {language === 'en' ? event.title : event.title_hi}
                  </h3>
                  <p className="text-sm md:text-base text-amber-800 mb-4">
                    {language === 'en' ? event.description : event.description_hi}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-amber-700">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>{event.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Events;